import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Platform,
  ImageBackground,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  Pressable,
  StyleSheet
} from 'react-native';
import {
  YStack,
  XStack,
  H1,
  H2,
  H4,
  Text,
  Button,
  SizableText,
  Card,
  Separator,
  H3,
  Tabs,
  Paragraph,
  Input,
  H6
} from 'tamagui';
import { LinearGradient } from 'expo-linear-gradient';
import { useSelector, useDispatch } from 'react-redux';
import { useNetInfo } from '@react-native-community/netinfo';
import { useNavigation, useRoute } from '@react-navigation/native';
import { FontAwesome, FontAwesome6, AntDesign } from '@expo/vector-icons';
import LottieView from '@utils/lottieAnimation';
import { registerAndPay } from "@api/api";
import loginUser from '@api/login';
import apis, { registerAndPay as registerUser } from '@api/api';
import createRandomGoogleUser from './LoginScreen/utils/createGoogleUser';
// Global imports
import styles from '@stylesheet';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import { DefaultRootState } from '@reduxLocal/persistState';
import {
  SHOW_AUTH_MODAL,
  HIDE_ALL_MODALS,
  SHOW_LOADING_MODAL,
  HIDE_LOADING_MODAL,
  LOGIN_FLIP,
  SUCCESS,
  SHOW_PASSWORD_RESET_MODAL,
  SET_LAST_VALID_USER_SESSION
} from '@reduxLocal/actions/types';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useResponsiveWeb } from '@hooks/useResponsiveWeb';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import StripePaymentComponent from './Components/StripePaymentComponent';
import PrimarySocialLoginButton from "./Components/Authorisation/PrimarySocialLoginButton";
import MicrosoftButton from './Components/Authorisation/MicrosoftButton';
import LinkedInButton from './Components/Authorisation/LinkedInButton';
import DiscordButton from './Components/Authorisation/DiscordButton';
import AppleButton from './Components/Authorisation/AppleButton';
import EmailSignUpButton from './Components/Authorisation/EmailSignUpButton';
import WhatsAppSignUpButton from './Components/Authorisation/WhatsAppSignUpButton';
import WhatsAppRegisterFlow from './Components/Authorisation/WhatsAppRegisterFlow';
import { PasskeyLoginButton } from '@components/passkey/PasskeyLoginButton';
import LoginSignInForm from './LoginScreen/Components/LoginSignInForm';
import { Formik } from 'formik';
import {
  handleOAuthResponse,
  navigatePostEmailSignupToEasyMode,
  navigateToRootDashboard,
} from './Components/Authorisation/utils/authUtils';
import { blurPreventionState } from '@utils/blurPrevention';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';

const WEB_BREAKPOINTS = {
  isMobile: false,
  isTablet: false,
  isDesktop: true,
  isLargeDesktop: true,
  isShortScreen: false,
  isMediumScreen: false,
  isTallScreen: true,
  isMobilePortrait: false,
  isMobileLandscape: false,
  isCompactHeight: false,
} as const;

const WEB_LAYOUT = {
  spacing: { xs: 8, sm: 12, md: 16, lg: 24, xl: 32 },
  typography: { xs: 12, sm: 14, base: 16, lg: 18, xl: 20 },
} as const;

const webClassName = (className: string) =>
  (Platform.OS === 'web' ? ({ className } as any) : {});

// Type definitions
interface RegistrationRouteParams {
  productId?: string;
  code?: string;
}

interface RegistrationFormData {
  // Personal Information
  fullName: string;
  email: string;
  phone?: string;

  // Business Information
  companyName: string;
  industry: string;
  companySize: string;
  role: string;

  // Interest and Goals
  primaryInterest: string;
  businessGoals: string[];
  currentTools: string[];

  // Technical Preferences
  deploymentPreference: string;
  integrationNeeds: string[];

  // Communication Preferences
  communicationPreference: string;
  marketingConsent: boolean;
  termsAccepted: boolean;
}

interface RegistrationProps {
  flipCard: () => void;
  onNewUserSignup?: (userData: any) => void;
  code?: string | { code: string };
  productId?: string;
  /** Web passkey sign-in: completes session the same way as password login (from LoginScreen). */
  onPasskeyLoginSuccess?: (result: { success?: boolean; data?: { id?: string } }) => void;
}

/**
 * Registration Screen Component
 * 
 * A comprehensive registration screen that captures user interest and business information
 * using the generateSchemaForm hook with modern responsive styling similar to LoginScreen and TutorialModal.
 * 
 * Features:
 * - Progressive form with logical grouping
 * - Responsive design for web and mobile
 * - Modern glassmorphism styling
 * - Integration with global stylesheet utilities
 * - Form validation and submission handling
 */
const Registration = ({ flipCard, onNewUserSignup, code, productId, onPasskeyLoginSuccess }: RegistrationProps) => {
  const navigation = useNavigation();
  const [activeState, setActiveState] = useState(null);

  // On web, layout is CSS-driven. Native uses responsive hooks.
  const responsiveWebHook = useResponsiveWeb();

  const responsiveBreakpoints = useMemo(() => {
    if (Platform.OS === 'web') {
      return WEB_BREAKPOINTS;
    }

    const { width, height } = responsiveWebHook;
    return {
      isMobile: width < 768,
      isTablet: width >= 768 && width < 1024,
      isDesktop: width >= 1024,
      isLargeDesktop: width >= 1440,
      isShortScreen: height < 600,
      isMediumScreen: height >= 600 && height < 900,
      isTallScreen: height >= 900,
      isMobilePortrait: width < 768 && height > width,
      isMobileLandscape: width < 768 && width > height,
      isCompactHeight: height < 700,
    };
  }, [
    Platform.OS === 'web' ? null : responsiveWebHook.width,
    Platform.OS === 'web' ? null : responsiveWebHook.height,
  ]);

  const windowDimensions = Platform.OS === 'web'
    ? { width: 1024, height: 768 }
    : { width: responsiveWebHook.width, height: responsiveWebHook.height };

  const getResponsivePadding = Platform.OS === 'web'
    ? (value: number) => value
    : responsiveWebHook.getResponsivePadding;

  const responsive = useMemo(() => (
    Platform.OS === 'web'
      ? WEB_LAYOUT
      : {
        spacing: {
          xs: responsiveBreakpoints.isMobile ? 4 : 6,
          sm: responsiveBreakpoints.isMobile ? 8 : 12,
          md: responsiveBreakpoints.isMobile ? 12 : 16,
          lg: responsiveBreakpoints.isMobile ? 16 : 24,
          xl: responsiveBreakpoints.isMobile ? 24 : 32,
        },
        typography: {
          xs: responsiveBreakpoints.isMobile ? 10 : 12,
          sm: responsiveBreakpoints.isMobile ? 12 : 14,
          base: responsiveBreakpoints.isMobile ? 14 : 16,
          lg: responsiveBreakpoints.isMobile ? 16 : 18,
          xl: responsiveBreakpoints.isMobile ? 18 : 20,
        }
      }
  ), [responsiveBreakpoints]);

  async function handleUserData(res) {
    let amendedData = res.data.data;

    if (Platform.OS !== 'web') {
      let activeTokens = amendedData.notification_info?.map(item => item.notification_token) || [];

      // TODO: Get notification token from appropriate service
      const notificationToken = ''; // Placeholder - should get actual token
      if (!activeTokens.length || activeTokens.includes(notificationToken)) {
        await apis.updateNotificationToken(notificationToken, amendedData.userData.id);
      }

      amendedData.expireAt = Date.now() + 1000 * 60 * 60 * 24 * 7;
    }
    return amendedData;
  }

  // Redux state and dispatch
  const dispatch = useDispatch();
  const theme = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);
  const netInfo = useNetInfo();
  const isConnected = Platform.OS === 'web' ? true : netInfo.isConnected;

  /** __DEV__ only — skip WhatsApp OTP and enter Easy Mode onboarding like a fresh WhatsApp signup. */
  const handleBypassWhatsApp = useCallback(async () => {
    if (!__DEV__) return;
    try {
      dispatch({ type: SHOW_LOADING_MODAL });
      const mockResponse = {
        data: {
          data: {
            userData: {
              id: 'whatsapp-bypass-user-123',
              _id: 'whatsapp-bypass-user-123',
              email: 'whatsapp+447700900123@whatsapp.smartsaas.local',
              name: '',
              surname: '',
              mobile: '447700900123',
              whatsappNumber: '447700900123',
              defaultCompany: null,
              favourites: [],
              security: { mfa: false, verified: true, authorised: true },
              city: null,
              country: '',
              recents: [],
              avatar: null,
              posts: [],
              affiliateCode: null,
              achievements: { achievement_points: 0, achievedList: [] },
              billing: {},
              lastLogin: null,
              authentications: {},
              tutorials: { tutorialModal: false },
              companies: [],
              user_initialised: false,
              profile_information: {
                name: '',
                surname: '',
                mobile: '447700900123',
              },
            },
            notification_info: [],
            company: null,
          },
        },
      };
      const amendedData = await handleUserData(mockResponse);
      await navigatePostEmailSignupToEasyMode(dispatch, navigation, amendedData, {
        signupMethod: 'whatsapp',
      });
    } catch (error) {
      console.error('Bypass WhatsApp error:', error);
    } finally {
      dispatch({ type: HIDE_LOADING_MODAL });
    }
  }, [dispatch, navigation]);

  useEffect(() => {
    const refreshLastValidSession = async () => {
      if (!isConnected) return;
      try {
        const storedUser = await AsyncStorage.getItem('userData');
        if (!storedUser) {
          dispatch({ type: SET_LAST_VALID_USER_SESSION, payload: null });
          return;
        }

        const parsedUser = JSON.parse(storedUser);
        const isExpired =
          parsedUser?.expireAt &&
          new Date(parsedUser.expireAt) <= new Date();

        if (isExpired) {
          await AsyncStorage.removeItem('userData');
          dispatch({ type: SET_LAST_VALID_USER_SESSION, payload: null });
          return;
        }

        dispatch({ type: SET_LAST_VALID_USER_SESSION, payload: parsedUser });
      } catch (error) {
        console.warn('Failed to refresh last valid user session:', error);
      }
    };

    void refreshLastValidSession();
  }, [dispatch, isConnected]);

  // Local state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);
  const [formData, setFormData] = useState<Partial<RegistrationFormData> | null>(null);
  const loginForm = useRef<any>(null);
  const [passwordHidden, setPasswordHidden] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [codeVerified, setCodeVerified] = useState(null);
  const notificationToken = useSelector((state: DefaultRootState) => state.app.notificationToken);

  // Blur prevention for web touch devices
  const emailInputRef = useRef<any>(null);
  const passwordInputRef = useRef<any>(null);
  const emailFocusTimeRef = useRef<number>(0);
  const passwordFocusTimeRef = useRef<number>(0);
  const isTouchDeviceRef = useRef<boolean>(false);

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      isTouchDeviceRef.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }
  }, []);

  const getCurrentTime = useCallback(() => {
    return Platform.OS === 'web' && typeof performance !== 'undefined' ? performance.now() : Date.now();
  }, []);

  // Blur prevention handlers for email input — native touch only.
  const handleEmailFocus = useCallback((_e: any) => {
    if (Platform.OS !== 'web' && isTouchDeviceRef.current) {
      emailFocusTimeRef.current = getCurrentTime();
      blurPreventionState.startPrevention();
    }
  }, [getCurrentTime]);

  const handleEmailBlur = useCallback((_e: any) => {
    if (Platform.OS !== 'web' && isTouchDeviceRef.current) {
      const now = getCurrentTime();
      const timeSinceFocus = now - emailFocusTimeRef.current;
      if (timeSinceFocus < 1000) {
        blurPreventionState.startPrevention();
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (emailInputRef.current) {
              try {
                emailInputRef.current.focus();
              } catch (err) {
                console.warn('Failed to refocus email input after false blur:', err);
              }
            }
          }, 10);
        });
        return;
      }
    }
    blurPreventionState.stopPrevention();
  }, [getCurrentTime]);

  const handlePasswordFocus = useCallback((_e: any) => {
    if (Platform.OS !== 'web' && isTouchDeviceRef.current) {
      passwordFocusTimeRef.current = getCurrentTime();
      blurPreventionState.startPrevention();
    }
  }, [getCurrentTime]);

  const handlePasswordBlur = useCallback((_e: any) => {
    if (Platform.OS !== 'web' && isTouchDeviceRef.current) {
      const now = getCurrentTime();
      const timeSinceFocus = now - passwordFocusTimeRef.current;
      if (timeSinceFocus < 1000) {
        blurPreventionState.startPrevention();
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (passwordInputRef.current) {
              try {
                passwordInputRef.current.focus();
              } catch (err) {
                console.warn('Failed to refocus password input after false blur:', err);
              }
            }
          }, 10);
        });
        return;
      }
    }
    blurPreventionState.stopPrevention();
  }, [getCurrentTime]);

  // Helper function to extract code string from various formats
  const getCodeString = useCallback((codeValue: string | { code: string } | undefined): string | undefined => {
    if (!codeValue) return undefined;
    let raw: string | undefined;
    if (typeof codeValue === 'string') raw = codeValue;
    else if (typeof codeValue === 'object' && 'code' in codeValue) raw = codeValue.code;

    if (raw && typeof raw === 'string') {
      // Sanitize: remove any trailing artifacts like ?? or other non-alphanumeric characters
      // that might be appended during deep link redirects
      return raw.trim().replace(/[?#]+$/, '');
    }
    return undefined;
  }, []);

  // Code verification useEffect
  useEffect(() => {
    const verifyCode = async () => {
      try {
        // Handle both string and object formats for code
        const codeString = getCodeString(code);
        if (!codeString) {
          setCodeVerified(null);
          return;
        }
        const res = await apis.verifyCouponCode(codeString);
        setCodeVerified(res.data);
      } catch (error: any) {
        if (error?.isDuplicate || error?.message?.includes('Duplicate request prevented')) {
          return;
        }
        console.error("Code verification failed:", error);
        setCodeVerified(null);
      }
    }

    if (code) {
      verifyCode();
    }
  }, [code, getCodeString]);

  // Refs
  const formRef = useRef<any>(null);
  const scrollViewRef = useRef<any>(null);
  const paymentRef = useRef<any>(null);

  useEffect(() => {
    setFormData(null);
  }, [])

  // REMOVED: console.log("Registration screen rendered") - component is memoized so shouldn't re-render unnecessarily

  // Memoize the registration schema to prevent GenerateSchemaForm from re-rendering unnecessarily
  const registrationSchema = useMemo(() => [{
    title: "fullName",
    type: "text",
    content: {
      label: "Full Name",
      validation: {
        label: "Full Name",
        required: true
      }
    },
  }, {
    title: "email",
    type: "email",
    content: { validation: { required: true } },
  }, {
    title: "contactNumber",
    type: "phone",
    content: {
      label: "Contact Number",
      validation: { required: true }
    },
  }, {
    title: "password",
    type: "password",
    content: {
      label: "Password",
      validation: {
        label: "Password",
        required: true,
        repeatPassword: true,
        minLength: 6,
        errorMessage: "Password must be at least 6 characters"
      }
    },
  }], []); // Empty deps - schema never changes

  // Memoize the submit function to prevent GenerateSchemaForm from re-rendering unnecessarily
  const handleRegistrationSubmit = useCallback(async (data) => {
    try {
      // Use the regular registerUser function instead of registerAndPay
      const response = await registerUser({
        ...data,
        productId,
        code,
        source: 'registration_screen',
        timestamp: new Date().toISOString()
      });
      // Set form data for success display
      setFormData(data);
      // Check if this is a new user registration
      // Since registerUser returns a function on error, check if response is not a function
      if (onNewUserSignup && typeof response !== 'function') {
        // Call the callback with user data
        onNewUserSignup({
          ...data,
          isNewUser: true,
          registrationResponse: response
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      // Handle error appropriately
      throw error;
    }
  }, [productId, code, onNewUserSignup]); // Only recreate if these dependencies change

  // const responsive = getResponsiveStyles();

  // Handle payment success
  const handlePaymentSuccess = useCallback((result) => {
    console.log('🎉 Payment successful:', result);
    dispatch({
      type: SHOW_AUTH_MODAL,
      data: {
        content: `Payment successful! Welcome to SmartSaaS ${result.product?.name || 'Premium'}. You can now access all features.`,
        type: "success"
      }
    });

    // Navigate to dashboard or login after successful payment
    setTimeout(() => {
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else if (flipCard) {
        flipCard();
      }
    }, 2000);
  }, [dispatch, navigation]);

  // Handle payment error
  const handlePaymentError = useCallback((error) => {
    console.error('💳 Payment error:', error);
    dispatch({
      type: SHOW_AUTH_MODAL,
      data: {
        content: error.message || "Payment failed. Please try again.",
        type: "error"
      }
    });
  }, [dispatch]);

  function handleResetPress() {
    dispatch({ type: SHOW_PASSWORD_RESET_MODAL })
  }

  // Handle payment cancellation
  const handlePaymentCancel = useCallback(() => {
    setFormData(null);
    // Just log, no need to show error for user cancellation
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async (values: RegistrationFormData) => {
    if (!isConnected) {
      dispatch({ type: HIDE_ALL_MODALS });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: "No network connection. Please check your internet connection and try again.",
          type: "error"
        }
      });
      return;
    }

    setIsSubmitting(true);
    dispatch({ type: SHOW_LOADING_MODAL });

    try {
      // Submit registration data
      const response = await registerUser({
        ...values,
        source: 'registration_screen',
        timestamp: new Date().toISOString()
      });

      // If response is a function, it means there was an error
      if (typeof response === 'function') {
        throw new Error('Registration failed');
      }

      // Success case
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: "Registration successful! Welcome to SmartSaaS. Please check your email for next steps.",
          type: "success"
        }
      });

      // Navigate to login or dashboard based on response
      setTimeout(() => {
        if (navigation.canGoBack()) {
          navigation.goBack();
        } else if (flipCard) {
          flipCard();
        }
      }, 2000);
    } catch (error) {
      console.error('Registration error:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: error.message || "Registration failed. Please try again.",
          type: "error"
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [isConnected, dispatch, navigation]);

  // // Generate the form using the schema
  // const RegistrationForm = generateSchemaForm({
  //   schema: registrationSchema,
  //   onSubmit: handleSubmit,
  //   submitText: "Complete Registration",
  //   hasTitle: true,
  //   columns: responsiveBreakpoints.isMobile ? 1 : 2,
  //   customStyling: {
  //     backgroundColor: 'transparent',
  //     padding: 0
  //   },
  //   scrollEnabled: true,
  //   resetOnSubmit: false,
  //   retainInitialValues: false,
  //   initialData: formData,
  //   buttonColor: '#3B82F6',
  //   textColor: theme ? '#1F2937' : '#F9FAFB'
  // });

  // Handle back navigation
  const handleBack = useCallback(() => {
    // Use a more generic navigation method
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      // Fallback or close modal
      flipCard?.();
    }
  }, [navigation, flipCard]);

  // Responsive styling
  const containerStyles = useMemo(() => ({
    ...(Platform.OS !== 'web' ? styles.modernCard : {}),
    ...(Platform.OS !== 'web' ? styles.loginMainCard : {}),
    backgroundColor: 'transparent',
    minHeight: Platform.OS === 'web' ? undefined : 500,
    ...(Platform.OS === 'web' ? {
    } : responsiveBreakpoints.isMobile ? {
      flex: 1,
      maxHeight: undefined,
      paddingBottom: 20,
    } : {
      maxHeight: windowDimensions.height * 0.7,
      maxWidth: Math.min(800, windowDimensions.width * 0.9),
    }),
    shadowOpacity: 0,
    margin: Platform.OS === 'web' ? 0 : (responsiveBreakpoints.isMobile ? getResponsivePadding(8) : getResponsivePadding(20)),
    borderWidth: 0
  } as any), [windowDimensions, responsiveBreakpoints, getResponsivePadding]);

  const animationStyles = useMemo(() => ({
    ...styles.loginAnimationContainer,
    maxHeight: responsiveBreakpoints.isMobile ? 120 : 160,
    minHeight: responsiveBreakpoints.isMobile ? 80 : 120,
  }), [responsiveBreakpoints]);

  const themedColors = useMemo(() => ({
    inputPlaceholder: theme ? 'rgba(100, 116, 139, 0.7)' : 'rgba(148, 163, 184, 0.75)',
    inputBackground: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
    inputBorder: theme ? 'rgba(148, 163, 184, 0.4)' : 'rgba(148, 163, 184, 0.35)',
    inputText: theme ? '#0f172a' : '#e2e8f0',
    iconMuted: theme ? '#64748b' : '#94a3b8',
    primaryButton: '#3B82F6',
    cardBackground: 'transparent',
    cardBorder: theme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(148, 163, 184, 0.25)',
    primaryText: theme ? '#0f172a' : '#e2e8f0',
    secondaryText: theme ? '#334155' : '#cbd5e1',
    dividerChipBackground: theme ? 'rgba(255, 255, 255, 0.9)' : 'rgba(15, 23, 42, 0.9)',
    dividerChipBorder: theme ? 'rgba(148, 163, 184, 0.35)' : 'rgba(148, 163, 184, 0.3)',
    successBackground: theme ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.2)',
    successBorder: theme ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.4)',
    successText: theme ? '#064E3B' : '#ECFDF5',
    successSecondaryText: theme ? '#065F46' : '#A7F3D0',
    successIcon: theme ? '#059669' : '#34D399',
  }), [theme]);

  const handleWebLoginSubmit = useCallback((values: { email: string; password: string; rememberMe?: boolean }) => {
    dispatch(loginUser(values, (data: { id: undefined; success: boolean }) => {
      if (data.success) {
        navigation.navigate('Dashboard' as never);
      }
    }));
  }, [dispatch, navigation]);

  const handleWebLoginOffline = useCallback(() => {
    dispatch({ type: HIDE_ALL_MODALS });
    dispatch({
      type: SHOW_AUTH_MODAL,
      data: { content: 'No network connection', type: 'error' },
    });
  }, [dispatch]);

  const handlePasskeyError = useCallback((message: string) => {
    dispatch({
      type: SHOW_AUTH_MODAL,
      data: { content: message, type: 'error' },
    });
  }, [dispatch]);

  const FormWrapper: any = Platform.OS === 'web' ? View : ScrollView;
  const formWrapperProps = Platform.OS === 'web'
    ? { ...webClassName('login-registrationForm'), style: styles.loginFormContainer }
    : {
        showsVerticalScrollIndicator: false,
        style: [
          styles.loginFormContainer,
          responsiveBreakpoints.isMobile && { paddingBottom: 20 },
        ],
        contentContainerStyle: responsiveBreakpoints.isMobile ? {} : undefined,
        keyboardShouldPersistTaps: 'handled' as const,
        keyboardDismissMode: 'on-drag' as const,
      };

  const SocialWrapper: any = Platform.OS === 'web' ? View : ScrollView;
  const socialWrapperProps = Platform.OS === 'web'
    ? { ...webClassName('login-socialButtons'), style: styles.loginSocialScrollView }
    : {
        style: styles.loginSocialScrollView,
        contentContainerStyle: {
          paddingHorizontal: responsive.spacing.lg,
          gap: responsiveBreakpoints.isMobile ? responsive.spacing.sm : responsive.spacing.md,
        },
        showsVerticalScrollIndicator: false,
      };

  return (
    <View
      style={{ 
        flexGrow: Platform.OS === 'web' ? 0 : 1,
        flexShrink: Platform.OS === 'web' ? 0 : 1,
        flexBasis: Platform.OS === 'web' ? 'auto' : undefined,
        minHeight: Platform.OS === 'web' ? undefined : 400,
        width: '100%',
        maxWidth: Platform.OS === 'web' ? 400 : undefined,
        alignSelf: Platform.OS === 'web' ? 'center' : undefined,
        overflow: 'visible',
        shadowOpacity: 0,
      }}
      ref={scrollViewRef}
    >
      {/* Main Registration Card */}
      <View
        {...webClassName('login-registrationCard')}
        style={[containerStyles, {
          // paddingVertical: "10%",
          backgroundColor:"transparent",
          borderWidth: 1,
          flexGrow: Platform.OS === 'web' ? 0 : 1,
          flexShrink: Platform.OS === 'web' ? 0 : 1,
          width: '100%',
          maxWidth: Platform.OS === 'web' ? 400 : undefined,
          alignSelf: Platform.OS === 'web' ? 'center' : undefined,
          borderColor: "transparent",
        }]}
      >
        <FormWrapper {...formWrapperProps}>
          <Tabs
            defaultValue="sign-in"
            orientation="horizontal"
            flexDirection="column"
            width="100%"
            borderRadius="$4"
            backgroundColor="transparent"
            value={activeState === 'sign-up' || activeState === 'whatsapp-sign-up' ? 'social' : 'sign-in'}
          >
            {
              flipCard ?
                <></>
                : <H3 color={themedColors.primaryText}>{t('Please register before proceeding to checkout')}</H3>
            }

            <Tabs.Content backgroundColor={"transparent"} value="sign-in" padding="$2">
              {Platform.OS !== 'web' ? (
              <Formik
                innerRef={loginForm}
                initialValues={{
                  email: null,
                  password: null,
                }}
                validateOnChange={false}
                validateOnBlur={isTouchDeviceRef.current ? false : true}
                validateOnMount={false}
                onSubmit={async values => {
                  setTimeout(() => { setShowForgotPassword(true); }, 1000);
                  dispatch(loginUser(values, (data: { id: undefined; success: boolean }) => {
                    if (data.success) {
                      navigation.navigate('Dashboard' as never)
                    }
                    if (data?.id === undefined) {
                      console.log('no data stored in id')
                    }
                  }))
                }}
              >{({ handleChange, handleBlur, handleSubmit, values }) => (
                <YStack
                  flex={1}
                  style={styles.loginFormContainer}
                  >
                  {/* Enhanced Email/Password Login Form - Fixed at bottom */}
                  <YStack
                    id="emailPasswordLoginSection"
                    gap={20}
                    paddingBottom={15}
                  >
                    <>
                    <XStack accessible={false} style={StyleSheet.flatten([styles.loginInputContainer, {
                      backgroundColor: themedColors.inputBackground,
                      borderColor: themedColors.inputBorder,
                    }])}>
                      <Input
                          ref={emailInputRef}
                        style={StyleSheet.flatten([styles.loginInput, {
                          backgroundColor: themedColors.inputBackground,
                          borderColor: themedColors.inputBorder,
                          color: themedColors.inputText
                        }])}
                        placeholder={t('Email or WhatsApp number')}
                        testID="emailInput"
                        accessible={true}
                        accessibilityLabel="emailInput"
                        placeholderTextColor={themedColors.inputPlaceholder}
                        keyboardType="default"
                        autoCapitalize="none"
                        onChange={(e) => handleChange('email')(e.nativeEvent.text)}
                        componentName="email"
                          onFocus={handleEmailFocus}
                          onBlur={handleEmailBlur}
                          blurOnSubmit={false}
                      />
                    </XStack>
                    <XStack style={StyleSheet.flatten([styles.loginInputContainer, {
                      backgroundColor: themedColors.inputBackground,
                      borderColor: themedColors.inputBorder,
                    }])}>
                      <Input
                          ref={passwordInputRef}
                        style={StyleSheet.flatten([styles.loginInput, {
                          backgroundColor: themedColors.inputBackground,
                          borderColor: themedColors.inputBorder,
                          color: themedColors.inputText
                        }])}
                        placeholder="Enter your password"
                        testID="passwordInput"
                        accessibilityLabel="passwordInput"
                        placeholderTextColor={themedColors.inputPlaceholder}
                        secureTextEntry={passwordHidden}
                        onChange={(e) => handleChange('password')(e.nativeEvent.text)}
                        componentName="password"
                        autoCapitalize="none"
                          onFocus={handlePasswordFocus}
                          onBlur={handlePasswordBlur}
                          blurOnSubmit={false}
                      />
                      <TouchableOpacity
                        onPress={() => setPasswordHidden(!passwordHidden)}
                        style={styles.loginPasswordToggle}
                      >
                        <FontAwesome6
                          style={{
                            color: passwordHidden ? themedColors.iconMuted : themedColors.primaryButton,
                          }}
                          name={passwordHidden ? "eye" : "eye-slash"}
                          size={responsiveBreakpoints.isMobile ? 18 : 20}
                        />
                      </TouchableOpacity>
                    </XStack>
                    <Button
                        backgroundColor={themedColors.primaryButton}
                      size={responsiveBreakpoints.isMobile ? "$3" : "$4"}
                      testID="signInButton"
                      accessible={true}
                      accessibilityLabel="signInButton"
                      onPress={() => {
                        if (isConnected) {
                          handleSubmit()
                        } else {
                          dispatch({ type: HIDE_ALL_MODALS });
                          dispatch({
                            type: SHOW_AUTH_MODAL, data: {
                              content: "No network connection", type: "error"
                            }
                          })
                        }
                      }}
                      icon={<FontAwesome name="user" color={"white"} size={24} />}>
                      <Text style={styles.loginSubmitButtonText}>
                          {t('Sign In')}
                      </Text>
                      </Button>
                      {onPasskeyLoginSuccess ? (
                        <PasskeyLoginButton
                          email={typeof values.email === 'string' ? values.email.trim() : ''}
                          onSuccess={onPasskeyLoginSuccess}
                          onError={handlePasskeyError}
                        />
                      ) : null}
                    </>
                    {/* {
                        __DEV__ &&
                      <Button buttonType="link" onPress={() => navigation.navigate("Registration")} >
                        Registration page
                      </Button>
                      } */}
                    </YStack>
                    {/* Enhanced Social Login Section - Scrollable */}
                    <YStack
                      id="socialLoginSection"
                      space={responsive.spacing.sm}
                      style={styles.loginSocialSection}
                    >
                      {/* Modern Divider with Enhanced Typography */}
                      <XStack style={styles.loginSocialDividerContainer}>
                        <View style={styles.loginSocialDividerLine} />
                        <Text style={StyleSheet.flatten([styles.loginSocialDividerText, {
                          padding: 5,
                          paddingHorizontal: 10,
                          marginHorizontal: 10,
                          backgroundColor: themedColors.dividerChipBackground,
                          borderColor: themedColors.dividerChipBorder,
                          color: themedColors.secondaryText
                        }])}>
                          {t('Or Sign Up / Sign In with')}
                        </Text>
                        <View style={styles.loginSocialDividerLine} />
                      </XStack>

                      {/* Enhanced Social Login Buttons Container */}
                      <SocialWrapper {...socialWrapperProps}>
                        {codeVerified?.data ? (
                          <View style={{ height: 90 }}>
                            <Card 
                              height={90} 
                              marginBottom={Platform.select({ android: 20 })}
                              backgroundColor={themedColors.successBackground}
                              borderColor={themedColors.successBorder}
                              borderWidth={1}
                            >
                              <Card.Header top={-10}>
                                <XStack alignItems="center" space="$2" marginBottom={10}>
                                  <FontAwesome6 
                                    name="check-circle" 
                                    size={16} 
                                    color={themedColors.successIcon} 
                                  />
                                  <H4 style={{ fontSize: responsiveFontSize(16), fontWeight: "bold", color: themedColors.successText }}>
                                    {codeVerified?.data?.sharedBy?.name ? (
                                      'Thanks to ' + codeVerified.data.sharedBy.name
                                    ) : (
                                      "Promo detected!"
                                    )}
                                  </H4>
                                </XStack>
                                <XStack top={-15}>
                                  <H6 style={{ fontSize: responsiveFontSize(14), flex: 1, top: 5, lineHeight: 20, color: themedColors.successSecondaryText }}>
                                    {(() => {
                                      const coupon = codeVerified?.data?.coupon;
                                      const discount = coupon?.percent_off
                                        ? `${coupon.percent_off}% off`
                                        : coupon?.amount_off
                                          ? `$${(coupon.amount_off / 100).toFixed(2)} off`
                                          : '0% off';
                                      const duration = coupon?.duration_in_months
                                        ? ` for ${coupon.duration_in_months} months`
                                        : '';
                                      return `You have received ${discount} your subscription${duration}!`;
                                    })()}
                                  </H6>
                                </XStack>
                              </Card.Header>
                            </Card>
                          </View>
                        ) : null}
                        {codeVerified?.data && Platform.OS !== 'web' ? (
                          <LottieView source={require("@assets/animations/celebration.json")} loop={false} autoPlay style={{
                            pointerEvents: "none",
                            top: responsiveBreakpoints.isMobile ? -30 : -50,
                            minWidth: responsiveBreakpoints.isMobile ? 150 : 250,
                            zIndex: 10,
                            minHeight: responsiveBreakpoints.isMobile ? 150 : 250,
                            position: "absolute"
                          }} />
                        ) : null}

                        {/* Enhanced Responsive Social Login Buttons */}
                        <YStack
                          style={{
                            gap: responsiveBreakpoints.isMobile ? 8 : 10,
                            width: '100%',
                            maxWidth: 400,
                            alignSelf: 'center',
                          }}
                        >
                          <PrimarySocialLoginButton code={codeVerified?.data ? (getCodeString(code) || codeVerified.data.code) : undefined} productId={productId} />
                          {/* <MicrosoftButton code={codeVerified?.data ? (getCodeString(code) || codeVerified.data.code) : undefined} />
                          <LinkedInButton productId={productId} code={codeVerified?.data ? (getCodeString(code) || codeVerified.data.code) : undefined} />
                          <DiscordButton productId={productId} code={codeVerified?.data ? (getCodeString(code) || codeVerified.data.code) : undefined} /> */}
                          <AppleButton productId={productId} code={codeVerified?.data ? (getCodeString(code) || codeVerified.data.code) : undefined} />
                          <EmailSignUpButton onPress={() => setActiveState('sign-up')} />
                          <WhatsAppSignUpButton onPress={() => setActiveState('whatsapp-sign-up')} />
                        </YStack>
                        {__DEV__ ? (
                          <XStack
                            flex={1}
                            space={responsiveBreakpoints.isMobile ? 12 : 20}
                            flexWrap="wrap"
                            rowGap={20}
                            justifyContent="center"
                            style={{
                              backgroundColor: 'rgba(239, 68, 68, 0.05)',
                              padding: responsive.spacing.md,
                              borderRadius: 12,
                              borderWidth: 1,
                              borderColor: 'rgba(239, 68, 68, 0.2)',
                              marginTop: responsive.spacing.sm,
                            }}
                          >
                            <Button
                              size={responsiveBreakpoints.isMobile ? "$2" : "$3"}
                              backgroundColor="#dc2626"
                              onPress={async () => {
                                try {
                                  // Create mock user payload for offline bypass
                                  const mockResponse = {
                                    data: {
                                      data: {
                                        userData: {
                                          id: "offline-bypass-user-123",
                                          _id: "offline-bypass-user-123",
                                          email: "offline@smartsaas.co.uk",
                                          name: "Offline",
                                          surname: "User",
                                          defaultCompany: null,
                                          favourites: [],
                                          security: { mfa: false },
                                          city: null,
                                          country: "",
                                          recents: [],
                                          avatar: null,
                                          posts: [],
                                          affiliateCode: null,
                                          achievements: {
                                            achievement_points: 0,
                                            achievedList: []
                                          },
                                          billing: {},
                                          lastLogin: null,
                                          authentications: {},
                                          tutorials: {
                                            tutorialModal: false
                                          },
                                          companies: []
                                        },
                                        notification_info: [],
                                        company: null
                                      }
                                    }
                                  };

                                  // Process the mock data the same way as real API response
                                  const amendedData = await handleUserData(mockResponse);

                                  // Dispatch SUCCESS to update Redux state
                                  dispatch({ type: SUCCESS, data: amendedData });

                                  // Navigate to Dashboard (use replace to avoid back navigation to login)
                                  (navigation as any).replace('Dashboard', {
                                    screen: 'Welcome',
                                    params: { session: amendedData }
                                  });
                                } catch (error) {
                                  console.error('Bypass Offline error:', error);
                                }
                              }}
                            >Bypass Offline
                            </Button>
                            <Button
                              size={responsiveBreakpoints.isMobile ? "$2" : "$3"}
                              onPress={async () => {
                                const res = await apis.handleOAuth({
                                  "id": "123",
                                  "email": "fareed@smartsaas.co.uk",
                                  "name": "John",
                                  "surname": "Doe",
                                  "oauthToken": "token123",
                                  "type": "basic",
                                  "notificationToken": "notif123",
                                  "device": {
                                    "brand": "Apple",
                                    "model": "iPhone 13",
                                    "os": "iOS",
                                    "osVersion": "17.3"
                                  }
                                })
                                const amendedData = await handleUserData(res);
                                dispatch({ type: SUCCESS, data: amendedData });
                                navigateToRootDashboard();
                              }}
                              backgroundColor="#059669"
                            >
                              Bypass Online
                            </Button>
                            <Button onPress={async () => {
                              try {
                                dispatch({ type: SHOW_LOADING_MODAL })
                                const randomGoogleUser = createRandomGoogleUser(notificationToken || "");
                                const res = await apis.handleOAuth(randomGoogleUser);
                                await handleOAuthResponse(res, dispatch, navigation, notificationToken || "", 'Google');
                              } catch (error) {
                                console.error('Google dev login error:', error);
                                dispatch({ type: HIDE_LOADING_MODAL })
                              }
                            }}>
                            Dev Login with Google
                            </Button>
                            <Button
                              size={responsiveBreakpoints.isMobile ? "$2" : "$3"}
                              backgroundColor="#25D366"
                              onPress={handleBypassWhatsApp}
                              testID="bypassWhatsAppButton"
                            >
                              Bypass WhatsApp
                            </Button>
                          </XStack>
                        ) : null}
                      </SocialWrapper>
                    </YStack>
                  </YStack>
                )}
              </Formik>
              ) : (
                <YStack flex={1} style={styles.loginFormContainer}>
                  <LoginSignInForm
                    theme={theme}
                    isConnected={isConnected}
                    onSubmit={handleWebLoginSubmit}
                    onPasskeyLoginSuccess={onPasskeyLoginSuccess}
                    onError={handlePasskeyError}
                    onOffline={handleWebLoginOffline}
                  />
                  <YStack
                    id="socialLoginSection"
                    space={responsive.spacing.sm}
                    style={styles.loginSocialSection}
                  >
                    <XStack style={styles.loginSocialDividerContainer}>
                      <View style={styles.loginSocialDividerLine} />
                      <Text style={StyleSheet.flatten([styles.loginSocialDividerText, {
                        padding: 5,
                        paddingHorizontal: 10,
                        marginHorizontal: 10,
                        backgroundColor: themedColors.dividerChipBackground,
                        borderColor: themedColors.dividerChipBorder,
                        color: themedColors.secondaryText
                      }])}>
                        {t('Or Sign Up / Sign In with')}
                      </Text>
                      <View style={styles.loginSocialDividerLine} />
                    </XStack>
                    <SocialWrapper {...socialWrapperProps}>
                      {codeVerified?.data ? (
                        <View style={{ height: 90 }}>
                          <Card
                            height={90}
                            backgroundColor={themedColors.successBackground}
                            borderColor={themedColors.successBorder}
                            borderWidth={1}
                          >
                            <Card.Header top={-10}>
                              <XStack alignItems="center" gap={8} marginBottom={10}>
                                <FontAwesome6 name="check-circle" size={16} color={themedColors.successIcon} />
                                <H4 style={{ fontSize: responsiveFontSize(16), fontWeight: 'bold', color: themedColors.successText }}>
                                  {codeVerified?.data?.sharedBy?.name
                                    ? `Thanks to ${codeVerified.data.sharedBy.name}`
                                    : 'Promo detected!'}
                                </H4>
                              </XStack>
                            </Card.Header>
                          </Card>
                        </View>
                      ) : null}
                      <YStack style={{ gap: 10, width: '100%', maxWidth: 400, alignSelf: 'center' }}>
                        <PrimarySocialLoginButton code={codeVerified?.data ? (getCodeString(code) || codeVerified.data.code) : undefined} productId={productId} />
                        <AppleButton productId={productId} code={codeVerified?.data ? (getCodeString(code) || codeVerified.data.code) : undefined} />
                        <EmailSignUpButton onPress={() => setActiveState('sign-up')} />
                        <WhatsAppSignUpButton onPress={() => setActiveState('whatsapp-sign-up')} />
                      </YStack>
                      {__DEV__ ? (
                        <XStack flex={1} flexWrap="wrap" rowGap={20} justifyContent="center" style={{ marginTop: responsive.spacing.sm }}>
                          <Button size="$3" backgroundColor="#dc2626" onPress={async () => {
                            try {
                              const mockResponse = { data: { data: { userData: { id: 'offline-bypass-user-123', _id: 'offline-bypass-user-123', email: 'offline@smartsaas.co.uk', name: 'Offline', surname: 'User', defaultCompany: null, favourites: [], security: { mfa: false }, city: null, country: '', recents: [], avatar: null, posts: [], affiliateCode: null, achievements: { achievement_points: 0, achievedList: [] }, billing: {}, lastLogin: null, authentications: {}, tutorials: { tutorialModal: false }, companies: [] }, notification_info: [], company: null } } };
                              const amendedData = await handleUserData(mockResponse);
                              dispatch({ type: SUCCESS, data: amendedData });
                              (navigation as any).replace('Dashboard', { screen: 'Welcome', params: { session: amendedData } });
                            } catch (error) {
                              console.error('Bypass Offline error:', error);
                            }
                          }}>Bypass Offline</Button>
                          <Button size="$3" backgroundColor="#059669" onPress={async () => {
                            const res = await apis.handleOAuth({ id: '123', email: 'fareed@smartsaas.co.uk', name: 'John', surname: 'Doe', oauthToken: 'token123', type: 'basic', notificationToken: 'notif123', device: { brand: 'Apple', model: 'iPhone 13', os: 'iOS', osVersion: '17.3' } });
                            const amendedData = await handleUserData(res);
                            dispatch({ type: SUCCESS, data: amendedData });
                            navigateToRootDashboard();
                          }}>Bypass Online</Button>
                          <Button
                            size="$3"
                            backgroundColor="#25D366"
                            onPress={handleBypassWhatsApp}
                            testID="bypassWhatsAppButton"
                          >
                            Bypass WhatsApp
                          </Button>
                        </XStack>
                      ) : null}
                    </SocialWrapper>
                  </YStack>
                </YStack>
              )}
            </Tabs.Content>

            {/* Social Login Tab */}
            <Tabs.Content value="social" padding="$2">
              <YStack space="$3" alignItems="center">
                {/* Code Detection Visual Indicator for Sign-up */}
                {codeVerified?.data ? (
                  <View style={{ height: 90, width: '100%' }}>
                    <Card 
                      height={90} 
                      marginBottom={Platform.select({ android: 20 })}
                      backgroundColor={themedColors.successBackground}
                      borderColor={themedColors.successBorder}
                      borderWidth={1}
                    >
                      <Card.Header top={-20}>
                        <XStack alignItems="center" space="$2" marginBottom={10}>
                          <FontAwesome6 
                            name="check-circle" 
                            size={16} 
                            color={themedColors.successIcon} 
                          />
                          <H4 style={{ fontSize: responsiveFontSize(16), fontWeight: "bold", color: themedColors.successText }}>
                            {codeVerified?.data?.sharedBy?.name ? (
                              'Thanks to ' + codeVerified.data.sharedBy.name
                            ) : (
                              "Promo detected!"
                            )}
                          </H4>
                        </XStack>
                        <XStack top={-15}>
                          <H6 style={{ fontSize: responsiveFontSize(14), flex: 1, top: 5, lineHeight: 20, color: themedColors.successSecondaryText }}>
                            {(() => {
                              const coupon = codeVerified?.data?.coupon;
                              const discount = coupon?.percent_off
                                ? `${coupon.percent_off}% off`
                                : coupon?.amount_off
                                  ? `$${(coupon.amount_off / 100).toFixed(2)} off`
                                  : '0% off';
                              const duration = coupon?.duration_in_months
                                ? ` for ${coupon.duration_in_months} months`
                                : '';
                              return `You have received ${discount} your subscription${duration}!`;
                            })()}
                          </H6>
                        </XStack>
                      </Card.Header>
                    </Card>
                  </View>
                ) : null}
                {codeVerified?.data && Platform.OS !== 'web' ? (
                  <LottieView source={require("@assets/animations/celebration.json")} loop={false} autoPlay style={{
                    pointerEvents: "none",
                    top: responsiveBreakpoints.isMobile ? -30 : -50,
                    minWidth: responsiveBreakpoints.isMobile ? 150 : 250,
                    zIndex: 10,
                    minHeight: responsiveBreakpoints.isMobile ? 150 : 250,
                    position: "absolute"
                  }} />
                ) : null}
                {activeState === 'whatsapp-sign-up' ? (
                  <WhatsAppRegisterFlow onCancel={() => setActiveState(null)} />
                ) : formData ? (
                  <View
                    style={{ transform: [{ scale: 0.8 }] }}
                  >
                    <H3 color={themedColors.primaryText}>{t('Thank you for registering!')}</H3>
                    <Paragraph color={themedColors.secondaryText}>
                      A confirmation email has been sent to {formData?.email}. Please check your inbox and follow the instructions to verify your email address and complete your registration.
                    </Paragraph>
                  </View>
                ) : (
                  <ScrollView
                    {...(Platform.OS === 'web' ? webClassName('login-emailSignupScroll') : {})}
                    style={{
                      width: '100%',
                      maxHeight: Platform.OS === 'web'
                        ? ('40vh' as any)
                        : responsiveWebHook.height * 0.5,
                      flexGrow: 0,
                      flexShrink: 1,
                    }}
                    contentContainerStyle={{
                      flexGrow: 0,
                      paddingBottom: 8,
                    }}
                    nestedScrollEnabled
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator
                  >
                    <GenerateSchemaForm
                      ref={formRef}
                      hideButton
                      buttonLabel='Create Account'
                      schema={registrationSchema}
                      submitFunction={handleRegistrationSubmit}
                    />
                  </ScrollView>
                )}
                <Separator
                  backgroundColor={theme ? '$borderColor' : 'rgba(255, 255, 255, 0.2)'}
                  marginVertical="$4"
                  width="80%"
                />
                <SizableText
                  size="$2"
                  textAlign="center"
                  color={themedColors.secondaryText}
                  maxWidth="80%"
                >
                  {t('By continuing with any option, you agree to our Terms of Service and Privacy Policy')}
                </SizableText>
              </YStack>
            </Tabs.Content>
          </Tabs>
        </FormWrapper>
        {/* Footer Section */}
        <YStack space="$3" paddingTop="$4">
          <XStack alignSelf='stretch' gap="$4" justifyContent="space-between" paddingHorizontal="$4">
            {(activeState === 'sign-up' || activeState === 'whatsapp-sign-up') && (
              <TouchableOpacity
                style={{
                  flex: 1,
                  backgroundColor: 'rgba(100, 116, 139, 0.4)', // subtle greyish translucent background
                  flexDirection: 'row',
                  padding: 12,
                  alignSelf: 'flex-start',
                  borderRadius: 8,
                  minWidth: 130,
                  gap: 10,
                  alignItems: 'center',
                }}
                onPress={() => setActiveState(null)}>
                  <FontAwesome6
                    name="arrow-left"
                    size={16}
                    color="white"
                  />
                <SizableText
                  size="$3"
                  color="white"
                  fontWeight="600"
                >
                  {t('Back to Sign In')}
                </SizableText>
              </TouchableOpacity>
            )}
            {activeState === 'sign-up' && !formData ? (
              <TouchableOpacity
                style={{
                  flex:1,
                  alignSelf: 'flex-end',
                  backgroundColor: '#3B82F6',
                  padding: 12,
                  borderRadius: 8,
                  minWidth: 100,
                  alignItems: 'center'
                }}
                onPress={() => {
                  if (formRef.current) {
                    try {
                      // First validate the form
                      formRef.current.validateForm().then((errors) => {
                        if (Object.keys(errors).length === 0) {
                          // No validation errors, proceed with submission
                          formRef.current.submitForm();
                        } else {
                          // Validation errors exist
                          // Set touched fields to show errors
                          formRef.current.setTouched(
                            Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
                          );
                        }
                      });
                    } catch (error) {
                      // Try handleSubmit as fallback
                      if (formRef.current.handleSubmit) {
                        formRef.current.handleSubmit();
                      }
                    }
                  } else {
                    console.log("formRef.current is null or undefined");
                  }
                }}>
                <XStack space="$2" alignItems="center">
                    <SizableText
                      size="$3"
                      color="white"
                      fontWeight="600"
                    >
                    {t('Register')}
                    </SizableText>
                    <FontAwesome6
                      name="arrow-right"
                      size={16}
                      color="white"
                    />
                  </XStack>
                </TouchableOpacity>
            ) : null}
          </XStack>
        </YStack>
      </View>
    </View>
  );
};

export default memo(Registration);
